<template>
  <dx-modal :value="show" title="Marcar como enviado" size="sm" id="modal-batch-send"
            :closable="!saving" @input="close()">
    <div class="stenci-group stenci-alert">
      <p>
        Registre aqui o envio feito no portal ou webservice da operadora. A partir daí o lote
        fica travado e o próximo passo é importar o demonstrativo de análise.
      </p>
    </div>

    <div class="cr-box send-context">
      <b>Lote {{ batch.number }}</b>
      · {{ guideTypeName(batch.guideType) }}
      · {{ batch.quantity }} {{ batch.quantity === 1 ? 'guia' : 'guias' }}
      · <span class="num text-bold">{{ batch.totals.informed | currency }}</span>
      <div class="mt-1" v-if="tissEnabled">
        <span class="file-chip" v-if="xmlGenerated">
          <fa-icon :icon="['fal', 'file-alt']"/>
          XML gerado em {{ batch.file.generatedAt | date }}
          <code v-if="batch.file.hash">{{ shortHash }}</code>
        </span>
        <span class="pill pill-pending" v-else>
          <fa-icon :icon="['fal', 'exclamation-triangle']"/>XML ainda não gerado
        </span>
        <a href="#" class="ml-1 text-small" v-if="!xmlGenerated"
           @click.prevent="$emit('generate-xml')">
          Gerar agora
        </a>
      </div>
    </div>

    <div class="columns">
      <div class="column col-12 form-group" :class="{'has-error': $v.form.sentAt.$error}">
        <label class="form-label" for="batch-sent-at">Data de envio</label>
        <dx-input-date id="batch-sent-at" class="form-input" placeholder="00/00/0000"
                       v-model="form.sentAt" @blur="$v.form.sentAt.$touch()"/>
        <template v-if="$v.form.sentAt.$error">
          <div class="form-input-hint" v-if="!$v.form.sentAt.required">Campo obrigatório</div>
          <div class="form-input-hint" v-else-if="!$v.form.sentAt.date">Data inválida</div>
          <div class="form-input-hint" v-else>A data de envio não pode ser futura</div>
        </template>
      </div>
      <div class="column col-7 col-sm-12 form-group">
        <label class="form-label" for="batch-protocol-number">Nº do protocolo</label>
        <input id="batch-protocol-number" type="text" class="form-input" maxlength="20"
               placeholder="Opcional" v-model="form.protocolNumber">
        <div class="form-input-hint">
          Se a operadora já devolveu o protocolo, informe agora; senão registre depois no detalhe.
        </div>
      </div>
      <div class="column col-5 col-sm-12 form-group"
           :class="{'has-error': $v.form.protocolDate.$error}">
        <label class="form-label" for="batch-protocol-date">Data do protocolo</label>
        <dx-input-date id="batch-protocol-date" class="form-input" placeholder="00/00/0000"
                       v-model="form.protocolDate" :disabled="!form.protocolNumber"
                       @blur="$v.form.protocolDate.$touch()"/>
        <template v-if="$v.form.protocolDate.$error">
          <div class="form-input-hint" v-if="!$v.form.protocolDate.required">Campo obrigatório</div>
          <div class="form-input-hint" v-else>Data inválida</div>
        </template>
      </div>
    </div>

    <template slot="footer">
      <button class="btn btn-primary btn-icon btn-icon-left mr-1"
              :class="{loading: saving}" :disabled="saving || (tissEnabled && !xmlGenerated)"
              @click="save">
        <fa-icon :icon="['fal', 'paper-plane']"/>Confirmar envio
      </button>
      <button class="btn" :disabled="saving" @click="close()">Sair</button>
    </template>
  </dx-modal>
</template>

<script>
import moment from 'moment';
import { required, requiredIf } from 'vuelidate/lib/validators';
import { date, maxDate } from '@/data/validators';
import { guideTypeName } from '../service';

export default {
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    batch: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      saving: false,
      form: {
        sentAt: moment().format('YYYY-MM-DD'),
        protocolNumber: '',
        protocolDate: '',
      },
    };
  },
  computed: {
    tissEnabled() {
      return !!(this.batch.tiss && this.batch.tiss.enabled);
    },
    xmlGenerated() {
      return !!(this.batch.file && this.batch.file.generatedAt);
    },
    shortHash() {
      const hash = this.batch.file.hash || '';
      return hash.length > 12 ? `${hash.slice(0, 6)}…${hash.slice(-4)}` : hash;
    },
  },
  validations() {
    return {
      form: {
        sentAt: {
          required,
          date,
          maxDate: maxDate(moment().format('YYYY-MM-DD')),
        },
        protocolDate: {
          required: requiredIf(() => !!this.form.protocolNumber),
          date,
        },
      },
    };
  },
  watch: {
    'form.protocolNumber': function onProtocolNumber(value) {
      if (value && !this.form.protocolDate) {
        this.form.protocolDate = this.form.sentAt || moment().format('YYYY-MM-DD');
      }
      if (!value) {
        this.form.protocolDate = '';
        this.$v.form.protocolDate.$reset();
      }
    },
  },
  methods: {
    guideTypeName,
    save() {
      this.$v.form.$touch();
      if (this.$v.form.$error) return;

      this.saving = true;
      const payload = {
        sentAt: this.form.sentAt,
        protocolNumber: this.form.protocolNumber.trim() || null,
        protocolDate: this.form.protocolNumber.trim() ? this.form.protocolDate : null,
      };

      this.$http.post(`/billing-batches/${this.batch.id}/send`, payload)
        .then(({ data }) => {
          this.$toast.success(payload.protocolNumber
            ? `Lote marcado como enviado · protocolo ${payload.protocolNumber}.`
            : 'Lote marcado como enviado.');
          this.$emit('sent', data);
          this.$emit('close', true);
        })
        .catch((e) => {
          this.$toast.error(e);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    close(sent = false) {
      if (this.saving) return;
      this.$emit('close', sent);
    },
  },
};
</script>

<style lang="scss">
@import "../batch.scss";

#modal-batch-send {
  .send-context {
    font-size: $font-size-sm;
    margin: 0 0 $layout-spacing-lg;
  }
}
</style>
